import { AxiosResponse } from 'axios';
import { SET_DONATIONS, SET_DONATION, SET_ERRORMSG } from '@/store/mutation_types';
import {
  BULK_DELETE_DONATIONS,
  BULK_MOVE_DONATIONS_TO_FOLDER,
  CLEAR_DONATION_ISLOADING,
  DELETE_DONATION,
  EDIT_DONATION,
  EXPORT_DONATIONS,
  GET_DONATIONS,
  MOVE_DONATION_TO_FOLDER,
  REGISTER_DONATION,
  TOGGLE_DONATION_IS_ACTIVE,
} from '@/store/action_types';
import { StateObject } from '@/dtos/state.dto';
import Donation from '@/services/donation';
import { Module } from 'vuex';
import { EditDonation, MoveDonationToFolder, MoveDonationsToFolder, RegisterDonation, GetDonation } from '@/dtos/donation.dto';

const buildPaginationMeta = (metaData: any, payload: any) => {
  const perPage = Number(metaData?.perPage ?? payload?.limit ?? 20);
  const currentPage = Number(metaData?.currentPage ?? payload?.page ?? 1);
  const totalRecords = Number(metaData?.totalRecords ?? 0);
  const totalPages = Number(metaData?.totalPages ?? Math.max(Math.ceil(totalRecords / perPage), 1));

  return { currentPage, perPage, totalRecords, totalPages };
};

// Each list fetch takes a ticket and only the newest one may write the list,
// so a slower request that was superseded (an earlier sort, search, filter or
// page) can never land on top of the rows the user asked for last.
let latestDonationsRequest = 0;

const donation: Module<any, {}> = {
  state: {
    donation: {
      data: null,
      isLoading: false,
      mataData: null
    },
    donations: {
      data: null,
      metaData: null,
      isLoading: false,
    },
  },
  getters: {
    donation: (state: { donation: any }) => state.donation,
    donations: (state: { donations: any }) => state.donations,
  },
  mutations: {
    [SET_DONATION]: (
      state: { donation: StateObject },
      payload: { data: null; status: any; isLoading: any }
    ) => {
      payload.data != null ? (state.donation.data = payload.data) : null,
        (state.donation.isLoading = payload.isLoading);
    },
    [SET_DONATIONS]: (
      state: { donations: StateObject },
      payload: { data: null; metaData?: any; status: any; isLoading: any }
    ) => {
      payload.data != null ? (state.donations.data = payload.data) : null,
        payload.metaData != null ? (state.donations.metaData = payload.metaData) : null,
        (state.donations.isLoading = payload.isLoading);
    },
  },
  actions: {
    [REGISTER_DONATION]: async ({ commit }: any, payload: any) => {
      commit(SET_DONATION, { data: null, isLoading: true });

      const result: AxiosResponse = await Donation.registerDonation(
        payload as RegisterDonation
      );

      if (result) {
        if (result.data.status) {
          commit(SET_DONATION, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_DONATION, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_DONATION, { isLoading: false });
        return false;
      }
    },
    [GET_DONATIONS]: async ({ commit }: any, payload: any) => {
      const request = ++latestDonationsRequest;
      commit(SET_DONATIONS, { data: null, isLoading: true });

      const result: AxiosResponse = await Donation.getDonations(
        payload as GetDonation
      );

      if (request !== latestDonationsRequest) return false;

      if (result) {
        if (result.data.status) {
          const records = Array.isArray(result.data.data) ? result.data.data : [];
          const metaData = buildPaginationMeta(result.data.metaData, payload);
          commit(SET_DONATIONS, { data: records, metaData, isLoading: false });
          return true;
        }
        commit(SET_DONATIONS, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_DONATIONS, { isLoading: false });
        return false;
      }
    },
    // Fetches donations for export WITHOUT mutating the paginated `donations`
    // list that backs the visible table, so the table behind the export modal
    // stays intact. Returns the raw records array (or null on failure).
    [EXPORT_DONATIONS]: async (_ctx: any, payload: any) => {
      const result: AxiosResponse = await Donation.getDonations(
        payload as GetDonation
      );

      if (result && result.data && result.data.status) {
        return Array.isArray(result.data.data) ? result.data.data : [];
      }

      return null;
    },
    [TOGGLE_DONATION_IS_ACTIVE]: async ({ commit, state }: any, payload: any) => {
      commit(SET_DONATION, { data: { id: payload.id }, isLoading: true });

      const result: AxiosResponse = await Donation.toggleDonationIsActive(
        payload as EditDonation
      );

      if (result) {
        if (result.data.status) {
          let index;
          if (state.donations.data) {
            state.donations.data.forEach((e: any, i: number) => {
              if (e.id == result.data.data.id) {
                index = i;
              }
            });
            if (index != null)
              state.donations.data[index].isActive = result.data.data.isActive;
          }
          commit(SET_DONATION, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_DONATION, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_DONATION, { isLoading: false });
        return false;
      }
    },
    [MOVE_DONATION_TO_FOLDER]: async ({ commit }: any, payload: any) => {
      commit(SET_DONATION, { data: { id: payload.id }, isLoading: true });

      let result: AxiosResponse;
      try {
        result = await Donation.moveDonationToFolder(
          payload as MoveDonationToFolder
        );
      } catch (error) {
        commit(SET_DONATION, { data: null, isLoading: false });
        throw error;
      }

      if (result) {
        if (result.data.status) {
          commit(SET_DONATION, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_DONATION, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_DONATION, { isLoading: false });
        return false;
      }
    },
    [BULK_MOVE_DONATIONS_TO_FOLDER]: async ({ commit }: any, payload: MoveDonationsToFolder) => {
      commit(SET_DONATION, { data: null, isLoading: true });

      let result: AxiosResponse;
      try {
        result = await Donation.moveDonationsToFolder(payload);
      } catch (error) {
        commit(SET_DONATION, { data: null, isLoading: false });
        throw error;
      }

      if (result?.data?.status) {
        commit(SET_DONATION, { data: result.data.data, isLoading: false });
        return true;
      }

      commit(SET_DONATION, { data: null, isLoading: false });
      if (result?.data?.msg) commit(SET_ERRORMSG, result.data.msg);
      return false;
    },
    [EDIT_DONATION]: async ({ commit }: any, payload: any) => {
      commit(SET_DONATION, { data: null, isLoading: true });

      const result: AxiosResponse = await Donation.editDonation(
        payload as EditDonation
      );

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data);
          commit(SET_DONATION, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_DONATION, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_DONATION, { isLoading: false });
        return false;
      }
    },
    [DELETE_DONATION]: async ({ commit }: any, payload: string) => {
      commit(SET_DONATION, { data: null, isLoading: true });

      let result: AxiosResponse;
      try {
        result = await Donation.deleteDonation(payload);
      } catch (error) {
        commit(SET_DONATION, { data: null, isLoading: false });
        throw error;
      }

      if (result) {
        if (result.data.status) {
          console.log('result.data.data', result.data.data);
          commit(SET_DONATION, { data: result.data.data, isLoading: false });
          return true;
        }
        commit(SET_DONATION, { data: null, isLoading: false });
        commit(SET_ERRORMSG, result.data.msg);
        return false;
      } else {
        commit(SET_DONATION, { isLoading: false });
        return false;
      }
    },
    [BULK_DELETE_DONATIONS]: async ({ commit }: any, ids: string[]) => {
      commit(SET_DONATION, { data: null, isLoading: true });

      let result: AxiosResponse;
      try {
        result = await Donation.deleteDonations(ids);
      } catch (error) {
        commit(SET_DONATION, { data: null, isLoading: false });
        throw error;
      }

      if (result?.data?.status) {
        commit(SET_DONATION, { data: result.data.data, isLoading: false });
        return true;
      }

      commit(SET_DONATION, { data: null, isLoading: false });
      if (result?.data?.msg) commit(SET_ERRORMSG, result.data.msg);
      return false;
    },
    [CLEAR_DONATION_ISLOADING]: ({ commit }) => {
      commit(SET_DONATION, { isLoading: false });
      commit(SET_DONATIONS, { isLoading: false });
    },
  },
};

export default donation;
